import { useCallback } from 'react'

import { useUnit } from 'effector-react'
import { useQuery } from 'react-query'

import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $activeChatDetails } from '@/models/chattingUI/chats/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { useUpdateNotice } from '@/modules/chatting/hooks/use-update-notice'
import { fanInfoService } from '@/modules/chatting/service/fan-info.ts'
import type { PpvStatistics } from '@/modules/chatting/types.ts'
import { GlobalInfo } from '@/modules/chatting/ui/chat-window/fan-info/global-info.tsx'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { handleError } from '@/utils/error.ts'

import { LastChatSummary } from './LastChatSummary.tsx'
import { FanDetailsCard } from './fan-details'
import { FanLists } from './lists'
import { Notice } from './notice'
import { PPVStats } from './ppv-stats/index.tsx'

const emptyStatistics: PpvStatistics = {
  averagePrice: 0,
  boughtCount: 0,
  maximumPrice: 0,
  totalCount: 0,
  lastPurchase: null,
}

export const FanInfoContent = () => {
  const creatorId = useUnit($creatorId)
  const {
    showFanDetails,
    showGlobalInfo,
    customNotePreset,
    customNotePresetEnabled,
  } = useSettingsContext()
  const activeChat = useUnit($activeChatDetails)

  const fan = activeChat?.withUser
  const fanId = fan?.id

  const updateNoticeMutation = useUpdateNotice(fanId)

  const { data: ppvStatistics, refetch } = useQuery({
    queryKey: ['fan-ppv-statistics', creatorId, fanId],
    queryFn: () =>
      fanId && creatorId
        ? fanInfoService.getPpvStatistics(creatorId, fanId).catch((error) => {
            handleError(error)

            return emptyStatistics
          })
        : Promise.resolve(emptyStatistics),
    enabled: !!fanId && !!creatorId,
    onError: (error) =>
      handleError(error, 'Failed to get ppv statistics', refetch),
  })

  const handleUpdateNotice = (notice: string) => {
    updateNoticeMutation.mutate(notice)
  }
  const handleClickEditNotice = useCallback(
    (notice: string) => {
      openDialog({
        type: DIALOG_TYPE.NOTICE,
        notice,
        user: fan,
      })
    },
    [fan],
  )

  // Early return after all hooks
  if (!activeChat || !fan) {
    return <div>{showGlobalInfo && <GlobalInfo />}</div>
  }

  return (
    <div>
      {showGlobalInfo && <GlobalInfo />}
      <div className="bg-secondary-50 dark:bg-secondary-600 space-y-4 rounded-xl p-[18px] mt-4">
        {showFanDetails && <FanDetailsCard details={fan} />}
        <LastChatSummary />
        <FanLists fan={fan} />
        <Notice
          fanId={fan.id}
          notice={fan.notice ?? ''}
          onUpdate={handleUpdateNotice}
          isUpdating={updateNoticeMutation.isLoading}
          onOpenDialog={handleClickEditNotice}
          preset={
            customNotePresetEnabled && !!customNotePreset
              ? customNotePreset
              : undefined
          }
        />
        {ppvStatistics && <PPVStats data={ppvStatistics} />}
      </div>
    </div>
  )
}
